import React, { useEffect } from 'react';
import Paper from '@gogame/ui/Paper';
import Box from '@gogame/ui/Box';
import { styled } from '@gogame/ui/styles';
import { useDraggable } from '@dnd-kit/core';
import { DraggableDialogContext } from './DraggableDialogContext';
import { MAX_HEIGHT, MIN_HEIGHT } from './constants';

const Container = styled('div', {
  shouldForwardProp: (prop) =>
    !['transform', 'position', 'conditions', '_recalcHeight', 'width'].includes(
      prop
    ),
})(({ theme, transform, position, _recalcHeight, conditions, width }) => {
  const newX = transform ? transform.x + position.x : position.x;
  const newY = transform ? transform.y + position.y : position.y;

  const normalizedX = Math.max(
    conditions.minX,
    Math.min(newX, conditions.maxX)
  );
  const normalizedY = Math.min(
    conditions.maxY,
    Math.max(newY, conditions.minY)
  );

  return {
    transform: `translate(${normalizedX}px, ${normalizedY}px)`,
    transition: transform ? 'none' : 'transform 150ms ease-out',
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: width,
    height: 'auto',
    zIndex: theme.zIndex.fab,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  };
});

const BoxSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minHeight: MIN_HEIGHT,
  maxHeight: MAX_HEIGHT,
});

export const DraggableDialog = React.forwardRef(
  ({ width, position, children, conditions }, ref) => {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
      id: 'draggable-ai-dialog',
    });

    return (
      <Container
        transform={transform}
        position={position}
        conditions={conditions}
        width={width}
        ref={setNodeRef}
      >
        <Paper
          elevation={8}
          ref={ref}
          sx={{
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <DraggableDialogContext.Provider value={{ attributes, listeners }}>
            <BoxSection component={'section'}>{children}</BoxSection>
          </DraggableDialogContext.Provider>
        </Paper>
      </Container>
    );
  }
);
